import { useState } from 'react';
import { apiPost } from '../../api/client';
import { AdminForm, ActionFeedback, Field, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Employee Creation — the admin surface for `POST /admin/employees`
 * (Mission 162), the one Employee lifecycle act this product had no way to
 * perform before.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS FORM DOES, AND WHAT IT DELIBERATELY DOES NOT
 *
 * Creates the Employee record only — a name and a start date, the two
 * facts the schema actually requires, plus three optional identifiers
 * (employee number, photo, level). It does not create a sign-in account
 * and does not place the new employee into a team or role: those are
 * `EmployeeIdentitySection` and `EmployeePlacementSection`'s own jobs,
 * immediately below. A new hire is created here first, then linked and
 * placed through those existing, already-built forms — never in one
 * combined wizard, the same "one form, one act" discipline every other
 * admin section on this page already follows.
 * ---------------------------------------------------------------------
 */
export function EmployeeCreationSection() {
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState<string | null>(null);

  return (
    <section className={styles.section} aria-label="Employee creation">
      <h2 className={styles.sectionTitle}>Employee creation</h2>
      <p className={styles.sectionNote}>
        Creates the employee record only — no sign-in account, and no team or role. Link an identity in Employee
        identity below, and set a team or role in Employee placement above, once the person exists here.
      </p>

      {lastCreated ? (
        <p className={styles.hint} role="status">
          {lastCreated} was created. They now appear in People and can be linked or placed.
        </p>
      ) : null}

      <button type="button" className={styles.secondaryButton} onClick={() => setCreating((open) => !open)}>
        {creating ? 'Close' : 'Create employee'}
      </button>

      {creating ? (
        <CreateEmployeeForm
          onCreated={(fullName) => {
            setCreating(false);
            setLastCreated(fullName);
          }}
        />
      ) : null}
    </section>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function CreateEmployeeForm({ onCreated }: { onCreated: (fullName: string) => void }) {
  const [fullName, setFullName] = useState('');
  const [employmentStartedOn, setEmploymentStartedOn] = useState(todayIso());
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [currentLevel, setCurrentLevel] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/employees', {
        fullName,
        employmentStartedOn,
        employeeNumber: employeeNumber.trim() || undefined,
        currentLevel: currentLevel.trim() || undefined,
      });
      return `${fullName} was created.`;
    });
    if (ok) onCreated(fullName);
  }

  return (
    <AdminForm title="Create employee" onSubmit={submit} submitLabel="Create" busy={state.status === 'saving'}>
      <Field id="employee-full-name" label="Full name" value={fullName} onChange={setFullName} />
      <Field
        id="employee-started-on"
        label="Employment start date (YYYY-MM-DD)"
        value={employmentStartedOn}
        onChange={setEmploymentStartedOn}
      />
      <Field
        id="employee-number"
        label="Employee number (optional)"
        value={employeeNumber}
        onChange={setEmployeeNumber}
        hint="An opaque reference — e.g. a payroll number. Left blank if this organisation does not use one."
      />
      <Field id="employee-level" label="Current level (optional)" value={currentLevel} onChange={setCurrentLevel} />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}
